import { MenuItem, ListItemText, TextField } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { FormSelectOption } from '../types';
import { GenericFormComponentProps } from '../types';
import formInputStyles from '../shared.module.scss';
import { useEffect } from 'react';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';
interface FormSelectProps extends GenericFormComponentProps {
    options: FormSelectOption[];
    customValueFormatter?: (...props: any[]) => any;
}

/**
 * component that allows a singular selection from a list of options
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {FormSelectOption[]} options - list of options to pick from
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @returns FormSelect component
 */
export function FormSelect({
    field,
    label,
    options,
    disabled,
    className,
    customValueFormatter = (value) => value,
}: FormSelectProps) {
    const { control, getFieldState, watch } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const watchedField = getFieldState(field);
    const fieldValue = watch(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                label = label || convertToDisplayName(field);
                const id = `form-select-${field}`;

                return (
                    <TextField
                        id={id}
                        label={label}
                        value={fieldProps.value}
                        disabled={disabled}
                        onChange={fieldProps.onChange}
                        className={`${formInputStyles.formInput} ${className}`}
                        variant="standard"
                        inputProps={{
                            ...fieldProps,
                            'data-testid': 'form-select',
                            multiple: false,
                            renderValue: (selected: string | number) => {
                                if (!selected && selected !== 0) {
                                    return options?.[0];
                                }
                                const chosenOption = options.find(
                                    (o) => o.value === selected
                                );

                                return chosenOption?.name ?? selected;
                            },
                        }}
                        select
                        color={error ? 'error' : 'primary'}
                        error={!!error}
                        helperText={error?.message}
                        defaultValue=""
                    >
                        {!!fieldValue &&
                            !options?.find((o) => o.value === fieldValue) && (
                                <MenuItem key="custom-value" value={fieldValue}>
                                    <ListItemText
                                        primary={`${customValueFormatter(
                                            fieldValue
                                        )} (custom setting)`}
                                    />
                                </MenuItem>
                            )}
                        {options?.map(({ name, value, disabled }, index) => {
                            const label = name ?? value;

                            return (
                                <MenuItem
                                    key={`${id}-options-${label}-${index}`}
                                    value={value}
                                    disabled={disabled}
                                >
                                    <ListItemText primary={label} />
                                </MenuItem>
                            );
                        })}
                    </TextField>
                );
            }}
        />
    );
}
